import { useEffect, useState } from 'react';

import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { Position, Quote } from 'src/models';
import { Maybe } from 'src/utils';

export interface UsePositionListSelectorReturnType {
  positionList: Position[];
  tradablePositionList: Position[];
  nonTradablePositionList: Position[];
  isPositionListLoading: boolean;
  hasPositionListError: boolean;
  positionListError?: string;
}

const EMPTY_POSITIONS: Position[] = [];

export const usePositionListSelector = (): UsePositionListSelectorReturnType => {
  const positionList: Maybe<Position[]> = useSelector((state: any) => state.positions.list.data?.data);
  const isPositionListLoading: boolean = useSelector((state: any) => Boolean(state.positions.list?.isLoading));
  const hasPositionListFailed: boolean = useSelector((state: any) => Boolean(state.positions.list?.__failed));
  const positionsError: string | undefined = useSelector((state: any) => state.positions.list?.message);
  const quoteListData: Maybe<Quote[]> = useSelector((state: any) => state.quotes.list.data?.data);
  const hasQuoteListError: boolean = useSelector((state: any) => Boolean(state.quotes.list?.__failed));
  const [tradableList, setTradableList] = useState<Position[]>([]);
  const [nonTradableList, setNonTradableList] = useState<Position[]>([]);
  const [tradableListWithQuotes, setTradableListWithQuotes] = useState<Position[]>([]);

  useEffect(() => {
    if (isEmpty(quoteListData)) {
      return;
    }

    const securitiesList: Position[] = tradableList.map((position: Position) => {
      const quoteSecurityData = quoteListData?.find((data: Quote) => data.symbol === position.symbol);
      const security: Position = {
        ...position,
        value: quoteSecurityData?.last || 0,
        totalValue: position.quantity * (quoteSecurityData?.last || 0),
        change: quoteSecurityData?.change || 0,
      };

      if (quoteSecurityData?.description) {
        security.description = quoteSecurityData?.description;
      }

      return security;
    });
    setTradableListWithQuotes(securitiesList);
  }, [quoteListData, tradableList]);

  useEffect(() => {
    if (positionList) {
      setTradableList(positionList.filter(position => position.isTradable));
      setTradableListWithQuotes(positionList.filter(position => position.isTradable));
      setNonTradableList(positionList.filter(position => !position.isTradable));
    }
  }, [positionList]);

  return {
    positionList: Array.isArray(positionList) ? positionList : EMPTY_POSITIONS,
    isPositionListLoading,
    hasPositionListError: hasPositionListFailed || hasQuoteListError, // TODO: separate quote list error and position list error
    positionListError: positionsError,
    tradablePositionList: tradableListWithQuotes,
    nonTradablePositionList: nonTradableList,
  };
};
